import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Box,
    Chip,
    Switch,
    FormControlLabel,
    Typography,
    alpha,
} from '@mui/material';

const EditUserDialog = ({
    open,
    onClose,
    user,
    onSubmit,
    organizations,
    availableRoles,
    getRoleDisplayName,
    colors,
    t,
}) => {
    const [roles, setRoles] = useState([]);
    const [orgId, setOrgId] = useState('');
    const [isAllowed, setIsAllowed] = useState(true);
    const [isActive, setIsActive] = useState(true);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user) {
            setRoles(user.roles || ['user']);
            setOrgId(user.organization_id || '');
            setIsAllowed(user.is_allowed ?? true);
            setIsActive(user.is_active ?? true);
        }
    }, [user, open]);

    if (!user) return null;

    const handleRoleChange = (event) => {
        const newRoles = event.target.value;
        const lastAdded = newRoles.find((r) => !roles.includes(r));

        if (lastAdded === 'user') {
            setRoles(newRoles.filter((r) => r !== 'admin' && r !== 'billing_admin' && r !== 'super_admin'));
        } else if (['admin', 'billing_admin', 'super_admin'].includes(lastAdded) && newRoles.includes('user')) {
            setRoles(newRoles.filter((r) => r !== 'user'));
        } else {
            setRoles(newRoles);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const data = {};
            if (JSON.stringify(roles) !== JSON.stringify(user.roles)) data.roles = roles;
            if (orgId && orgId !== user.organization_id) data.organization_id = orgId;
            if (isAllowed !== user.is_allowed) data.is_allowed = isAllowed;
            if (isActive !== user.is_active) data.is_active = isActive;

            await onSubmit(user.id, data);
        } finally {
            setLoading(false);
        }
    };

    const getRoleChipColor = (role) => {
        switch (role) {
            case 'super_admin': return { bg: alpha(colors.warning, 0.12), color: colors.warning };
            case 'admin': return { bg: alpha(colors.secondary, 0.12), color: colors.secondary };
            case 'billing_admin': return { bg: alpha(colors.info, 0.12), color: colors.info };
            default: return { bg: colors.backgroundAlt, color: colors.textSecondary };
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    border: `1px solid ${colors.border}`,
                    boxShadow: colors.isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.1)',
                },
            }}
        >
            <DialogTitle sx={{ pb: 1, pt: 3, px: 3, fontWeight: 600, fontSize: '1.25rem', color: colors.text }}>
                {t('edit_user', 'Edit User')}
            </DialogTitle>
            <DialogContent sx={{ p: 3, pt: 2 }}>
                <Typography variant="body2" sx={{ mb: 3, color: colors.textSecondary }}>
                    {user.email}
                </Typography>

                <FormControl fullWidth sx={{ mb: 2.5 }}>
                    <InputLabel>{t('organization', 'Organization')}</InputLabel>
                    <Select
                        value={orgId}
                        label={t('organization', 'Organization')}
                        onChange={(e) => setOrgId(e.target.value)}
                        sx={{ borderRadius: '8px' }}
                    >
                        {organizations.map((org) => (
                            <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2.5 }}>
                    <InputLabel>{t('roles', 'Roles')}</InputLabel>
                    <Select
                        multiple
                        value={roles}
                        onChange={handleRoleChange}
                        input={<OutlinedInput label={t('roles', 'Roles')} sx={{ borderRadius: '8px' }} />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => {
                                    const chipColor = getRoleChipColor(value);
                                    return (
                                        <Chip
                                            key={value}
                                            label={getRoleDisplayName(value)}
                                            sx={{ bgcolor: chipColor.bg, color: chipColor.color, fontWeight: 500 }}
                                        />
                                    );
                                })}
                            </Box>
                        )}
                    >
                        {availableRoles.map((role) => (
                            <MenuItem key={role} value={role}>{getRoleDisplayName(role)}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Box sx={{ display: 'flex', gap: 3 }}>
                    <FormControlLabel
                        control={<Switch checked={isAllowed} onChange={(e) => setIsAllowed(e.target.checked)} color="primary" />}
                        label={t('allowed', 'Allowed')}
                        sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500, color: colors.text } }}
                    />
                    <FormControlLabel
                        control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />}
                        label={t('active', 'Active')}
                        sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500, color: colors.text } }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
                <Button onClick={onClose} sx={{ color: colors.textSecondary, textTransform: 'none', fontWeight: 500 }}>
                    {t('cancel', 'Cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disableElevation
                    disabled={loading || !roles.length}
                    sx={{
                        bgcolor: colors.primary, color: 'white', textTransform: 'none',
                        fontWeight: 600, px: 3, py: 1, borderRadius: '8px',
                        '&:hover': { bgcolor: colors.primaryDark },
                        '&.Mui-disabled': { bgcolor: alpha(colors.primary, 0.6), color: 'white' },
                    }}
                >
                    {loading ? t('saving', 'Saving...') : t('save_changes', 'Save Changes')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditUserDialog;
